export const todayIso = () => new Date().toISOString().slice(0, 10)

export const nowTime = () => {
  const now = new Date()
  return `${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}`
}

export const formatTimestamp = (iso: string | null): string =>
  iso
    ? new Date(iso).toLocaleString(undefined, {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      })
    : '—'

const pad = (value: number) => String(value).padStart(2, '0')

export const localDayKey = (at: Date): string => `${at.getFullYear()}-${pad(at.getMonth() + 1)}-${pad(at.getDate())}`

export const isoToLocalDateTime = (iso: string): { date: string; time: string } => {
  const at = new Date(iso)
  return {
    date: localDayKey(at),
    time: `${pad(at.getHours())}:${pad(at.getMinutes())}`
  }
}

const shortDayFormatter = new Intl.DateTimeFormat('en-GB', { day: 'numeric', month: 'short', timeZone: 'UTC' })

const dayWithYearFormatter = new Intl.DateTimeFormat('en-GB', {
  day: 'numeric',
  month: 'short',
  year: 'numeric',
  timeZone: 'UTC'
})

// UTC, not local: these render a calendar day, and a local render shifts it west of UTC.
const formatUtcDay = (formatter: Intl.DateTimeFormat, value: string | null): string => {
  if (!value) return '—'
  const at = new Date(value)
  return Number.isNaN(at.getTime()) ? '—' : formatter.format(at)
}

// Year is dropped: callers render many date columns per row, and a step rarely spans a year.
export const formatShortDay = (value: string | null): string => formatUtcDay(shortDayFormatter, value)

export const formatDayWithYear = (value: string | null): string => formatUtcDay(dayWithYearFormatter, value)

export const HOUR_MS = 3_600_000

interface ElapsedUnit {
  unit: Intl.RelativeTimeFormatUnit
  ms: number
}

const MINUTE: ElapsedUnit = { unit: 'minute', ms: 60_000 }

// Descending, so the first match is the dominant unit.
const ELAPSED_UNITS: ElapsedUnit[] = [{ unit: 'day', ms: 24 * HOUR_MS }, { unit: 'hour', ms: HOUR_MS }, MINUTE]

const relativeTimeFormatter = new Intl.RelativeTimeFormat('en', { numeric: 'always', style: 'long' })

/** Single dominant unit, past only: "40 minutes ago", "3 days ago". */
export const formatTimeAgo = (value: string, now: Date = new Date()): string => {
  const at = new Date(value)
  if (Number.isNaN(at.getTime())) return '—'
  const elapsedMs = Math.max(now.getTime() - at.getTime(), 0)
  const { unit, ms } = ELAPSED_UNITS.find((candidate) => elapsedMs >= candidate.ms) ?? MINUTE
  return relativeTimeFormatter.format(-Math.floor(elapsedMs / ms), unit)
}

// `Intl.RelativeTimeFormat` has no bare-span mode. The strip needs both guarantees above: `en` for
// the suffix's language, and `numeric: 'always'` — `'auto'` yields "yesterday", which has none.
export const formatElapsed = (value: string, now: Date = new Date()): string =>
  formatTimeAgo(value, now).replace(/ ago$/, '')
